/**
 * Role-based visibility helpers.
 *
 * Defines who sees what across the trip-money UI.
 * Backend redaction of these fields will come in a later PR; this is the
 * frontend hide-only layer for now (verified safe because only super_admin
 * is using the system today; non-admin users come later).
 */

import { useAuth } from '@/lib/auth';

export type AdminRole = 'super_admin' | 'admin' | 'accountant';

const ADMIN_ROLES: ReadonlyArray<string> = ['super_admin', 'admin', 'accountant'];

/**
 * Can this role see the FULL money breakdown (Client Pays, Company Keeps,
 * Investor Net, Driver Pay)?
 *
 * Investors / drivers / supervisors should NOT see "Company Keeps" — that's
 * sensitive markup info. They see only their own row in self-views.
 */
export function canSeeFullMoneyBreakdown(role: string | null | undefined): boolean {
  return ADMIN_ROLES.includes(role ?? '');
}

/**
 * Can this role lock months / generate invoices?
 */
export function canLockMonth(role: string | null | undefined): boolean {
  return ADMIN_ROLES.includes(role ?? '');
}

/**
 * Hook wrapper for components.
 */
export function useIsAdmin(): boolean {
  const role = useAuth((s) => s.user?.role);
  return canSeeFullMoneyBreakdown(role);
}

/**
 * Can this role upload photos from the gallery (vs camera-only)?
 * Drivers must use the camera (anti-fraud). Admins and accountants can
 * upload from gallery for backfills.
 */
export function canUploadFromGallery(role: string | null | undefined): boolean {
  return ADMIN_ROLES.includes(role ?? '');
}

export function useCanUploadFromGallery(): boolean {
  const role = useAuth((s) => s.user?.role);
  return canUploadFromGallery(role);
}

// ─────────────────────────────────────────────────────────────────
// Delete gating (PR-MAY13-PERM-AUDIT) — centralized so future role
// changes (e.g. adding a new admin-tier role) flip every delete
// button at once. Both super_admin and admin may trigger non-force
// deletes; only super_admin may set ?force=true (DeleteConfirmDrawer
// gates the inner checkbox separately).
// ─────────────────────────────────────────────────────────────────
const DELETE_ROLES: ReadonlyArray<string> = ['super_admin', 'admin'];

export function canDelete(role: string | null | undefined): boolean {
  return DELETE_ROLES.includes(role ?? '');
}

export function canForceDelete(role: string | null | undefined): boolean {
  return role === 'super_admin';
}

export function useCanDelete(): boolean {
  const role = useAuth((s) => s.user?.role);
  return canDelete(role);
}

export function useCanForceDelete(): boolean {
  const role = useAuth((s) => s.user?.role);
  return canForceDelete(role);
}

